import { useState } from "react";
import { Box, Skeleton, Stack, Typography, useTheme } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import dayjs from "dayjs";
import ErrorDisplay from "../common/ErrorDisplay/ErrorDisplay";
import { SERIF_DISPLAY } from "../Chat/ChatHero";
import { heroRise } from "../Chat/chatMotion";
import { isSafeImageSrc } from "../../lib/safeImageSrc";
import { feedDate } from "./feedDate";
import { usePublications } from "./hooks/usePublications";
import { PublicationSummary } from "./types";

const clampLines = (lines: number) => ({
  display: "-webkit-box",
  WebkitLineClamp: lines,
  WebkitBoxOrient: "vertical" as const,
  overflow: "hidden",
});

interface HeroThumbProps {
  src: string | null;
}

/** The post's hero, cropped to 16:10; nothing at all when there is none or it fails to load. */
const HeroThumb = ({ src }: HeroThumbProps) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [failed, setFailed] = useState(false);
  if (!isSafeImageSrc(src) || failed) return null;

  return (
    <Box
      sx={{
        flex: "none",
        width: { xs: 96, sm: 152 },
        aspectRatio: "16 / 10",
        overflow: "hidden",
        borderRadius: 1.5,
        border: isDark
          ? "1px solid rgba(255, 255, 255, 0.08)"
          : "1px solid rgba(0, 0, 0, 0.08)",
        backgroundColor: isDark
          ? "rgba(255, 255, 255, 0.03)"
          : "rgba(0, 0, 0, 0.03)",
      }}
    >
      <Box
        component="img"
        src={src}
        alt=""
        loading="lazy"
        onError={() => setFailed(true)}
        className="publicationThumb"
        sx={{
          display: "block",
          width: "100%",
          height: "100%",
          objectFit: "cover",
          transition: "transform 0.6s cubic-bezier(0.22, 1, 0.36, 1)",
          "@media (prefers-reduced-motion: reduce)": { transition: "none" },
        }}
      />
    </Box>
  );
};

interface PostRowProps {
  post: PublicationSummary;
  delay: number;
}

/** One story in the feed: when, headline, two lines of summary, its picture. */
const PostRow = ({ post, delay }: PostRowProps) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  return (
    <Box component="li" sx={{ listStyle: "none", ...heroRise(delay) }}>
      <Box
        component={RouterLink}
        to={`/publications/${encodeURIComponent(post.slug)}`}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: { xs: 2, sm: 3 },
          px: 1.5,
          py: 2,
          textDecoration: "none",
          color: "inherit",
          WebkitTapHighlightColor: "transparent",
          transition: "background-color 0.2s ease",
          "&:hover": {
            backgroundColor: isDark
              ? "rgba(255, 255, 255, 0.03)"
              : "rgba(0, 0, 0, 0.025)",
          },
          "&:hover .publicationThumb": { transform: "scale(1.04)" },
          "&:hover .publicationTitle": {
            textDecorationColor: theme.palette.primary.main,
          },
          // Touch has no hover, so the press acknowledges the tap.
          "&:active": {
            backgroundColor: isDark
              ? "rgba(255, 255, 255, 0.05)"
              : "rgba(0, 0, 0, 0.04)",
          },
          "&:focus-visible": {
            outline: `2px solid ${theme.palette.primary.main}`,
            outlineOffset: -2,
          },
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {post.publishedAt && (
            <Typography
              component="time"
              dateTime={post.publishedAt}
              title={dayjs(post.publishedAt).format("D MMMM YYYY")}
              sx={{
                display: "block",
                mb: 0.5,
                fontSize: "0.75rem",
                fontWeight: 500,
                fontVariantNumeric: "tabular-nums",
                color: theme.palette.text.secondary,
              }}
            >
              {feedDate(post.publishedAt)}
            </Typography>
          )}
          <Typography
            component="h2"
            className="publicationTitle"
            sx={{
              fontSize: { xs: "1rem", sm: "1.0625rem" },
              fontWeight: 600,
              lineHeight: 1.35,
              letterSpacing: "-0.01em",
              color: theme.palette.text.primary,
              textDecorationLine: "underline",
              textDecorationThickness: "1.5px",
              textUnderlineOffset: "0.2em",
              textDecorationColor: "transparent",
              transition: "text-decoration-color 0.2s ease",
              ...clampLines(2),
            }}
          >
            {post.title}
          </Typography>
          <Typography
            sx={{
              mt: 0.5,
              fontSize: { xs: "0.8125rem", sm: "0.875rem" },
              lineHeight: 1.5,
              color: theme.palette.text.secondary,
              ...clampLines(2),
            }}
          >
            {post.summary}
          </Typography>
        </Box>
        <HeroThumb src={post.heroImageUrl} />
      </Box>
    </Box>
  );
};

/** Every published post, newest first, as a feed: one row each, ruled top and bottom. */
const PublicationsPage = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const rule = isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)";
  const { data: publications, isLoading, error, refetch } = usePublications();

  const posts = publications ?? [];
  const showError = Boolean(error) && !publications;
  const showEmpty = !isLoading && !showError && posts.length === 0;

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 760,
        mx: "auto",
        px: { xs: 2, sm: 3 },
        pt: { xs: 3, md: 6 },
        // Nothing sits flush against the foot of the page
        pb: 8,
      }}
    >
      <Typography
        component="h1"
        sx={{
          fontFamily: SERIF_DISPLAY,
          fontWeight: 500,
          fontSize: { xs: "1.75rem", md: "2.25rem" },
          letterSpacing: "-0.01em",
          lineHeight: 1.2,
          color: theme.palette.text.primary,
          ...heroRise(0),
        }}
      >
        Product updates
      </Typography>
      <Typography
        sx={{
          mt: 1,
          mb: { xs: 2, md: 3 },
          fontSize: "0.9375rem",
          color: theme.palette.text.secondary,
          ...heroRise(0.05),
        }}
      >
        What's new in Insight.
      </Typography>

      {isLoading && (
        <Stack spacing={0}>
          {[0, 1, 2].map((index) => (
            <Stack
              key={index}
              direction="row"
              spacing={3}
              alignItems="center"
              sx={{ py: 2 }}
            >
              <Stack spacing={0.75} sx={{ flex: 1 }}>
                <Skeleton variant="text" width={72} />
                <Skeleton variant="text" width="70%" height={24} />
                <Skeleton variant="text" width="90%" />
              </Stack>
              <Skeleton
                variant="rounded"
                sx={{
                  width: { xs: 96, sm: 152 },
                  aspectRatio: "16 / 10",
                  height: "auto",
                }}
              />
            </Stack>
          ))}
        </Stack>
      )}

      {showError && (
        <ErrorDisplay
          error={error}
          onRetry={() => void refetch()}
          variant="card"
          fullWidth
        />
      )}

      {showEmpty && (
        <Typography
          sx={{
            py: 8,
            textAlign: "center",
            fontSize: "0.875rem",
            color: theme.palette.text.secondary,
          }}
        >
          Nothing published yet.
        </Typography>
      )}

      {posts.length > 0 && (
        <Box
          component="ul"
          sx={{
            // The rows' text lines up with the heading; the rules and the
            // hover wash run a little wider.
            mx: -1.5,
            p: 0,
            my: 0,
            borderTop: `1px solid ${rule}`,
            borderBottom: `1px solid ${rule}`,
            "& > li + li": { borderTop: `1px solid ${rule}` },
          }}
        >
          {posts.map((post, index) => (
            <PostRow
              key={post.id}
              post={post}
              delay={0.1 + Math.min(index, 6) * 0.04}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default PublicationsPage;
